class VoltProductHotspots extends HTMLElement {
  connectedCallback() {
    const target = this.querySelector('[data-companion-products]');
    if (!target) return;

    const requestedLimit = Number.parseInt(this.dataset.companionProductLimit, 10);
    const limit = Number.isInteger(requestedLimit) ? requestedLimit : 3;
    const sources = [...this.querySelectorAll('[data-companion-product]')];

    sources.forEach((source, index) => {
      if (index >= limit) {
        source.remove();
        return;
      }

      source.hidden = false;
      target.append(source);
    });
  }
}

if (!customElements.get('volt-product-hotspots')) {
  customElements.define('volt-product-hotspots', VoltProductHotspots);
}

// Opt-in, non-modal product previews. Native Horizon purchasing remains separate.
class VoltProductHotspotPreview extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.trigger = this.querySelector('.hotspot-trigger');
    this.dialog = this.querySelector('.hotspot-dialog');
    if (!this.trigger || !this.dialog) return;
    this.trigger.setAttribute('aria-expanded', 'false');
    const listen = (target, event, handler) => target.addEventListener(event, handler, { signal: this.controller.signal });
    this.trigger.addEventListener('click', (event) => {
      event.preventDefault();
      this.clearTimers();
      if (this.dialog.open && this.pinned) {
        this.close(true);
      } else {
        this.open();
        this.pinned = true;
        this.querySelector('.hotspot-dialog__link')?.focus({ preventScroll: true });
      }
    }, { signal: this.controller.signal });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.dialog.open) {
        const ownsFocus = this.pinned || this.contains(document.activeElement);
        if (ownsFocus) event.preventDefault();
        this.close(ownsFocus);
      }
    }, { signal: this.controller.signal });
    listen(document, 'pointerdown', (event) => {
      if (this.dialog.open && !this.contains(event.target)) this.close();
    });
    listen(this, 'focusout', (event) => {
      if (!this.opening && !this.contains(event.relatedTarget)) this.close();
    });
    listen(this, 'focusin', () => clearTimeout(this.closeTimer));
    listen(window, 'resize', () => this.close());
    listen(window, 'blur', () => this.close());
    listen(this, 'pointerenter', () => {
      clearTimeout(this.closeTimer);
      if (!window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)').matches) return;
      this.openTimer = setTimeout(() => this.open(true), 120);
    });
    listen(this, 'pointerleave', () => {
      clearTimeout(this.openTimer);
      if (!this.pinned && !this.contains(document.activeElement)) {
        this.closeTimer = setTimeout(() => {
          if (!this.pinned && !this.contains(document.activeElement)) this.close();
        }, 150);
      }
    });
  }

  open(hover = false) {
    const container = this.closest('.hotspots-container');
    if (!container) return;
    const others = [...document.querySelectorAll('volt-product-hotspot-preview')];
    if (hover && (this.dialog.open || others.some((other) =>
      other !== this && other.dialog?.open && (other.pinned || other.contains(document.activeElement))))) return;
    others.forEach((other) => {
      if (other !== this) other.close();
    });
    const bounds = container.getBoundingClientRect();
    const host = this.getBoundingClientRect();
    this.style.setProperty('--preview-available-width', `${bounds.width - 16}px`);
    this.style.setProperty('--preview-available-height', `${bounds.height - 32}px`);
    if (!this.dialog.open) {
      this.opening = true;
      // show() autofocuses even non-modal dialogs; body.focus() cannot undo it.
      // Hover reveals the same non-modal dialog without running native autofocus.
      if (hover) this.dialog.setAttribute('open', '');
      else this.dialog.show();
      this.opening = false;
    }
    const card = this.dialog.getBoundingClientRect();
    const mobile = window.innerWidth < 750;
    const clamp = ([x, y]) => [
      Math.max(8, Math.min(x, bounds.width - card.width - 8)),
      Math.max(8, Math.min(y, bounds.height - card.height - 8)),
    ];
    const x = host.left - bounds.left;
    const y = host.top - bounds.top;
    const centerX = x + (host.width - card.width) / 2;
    let placement = clamp([(bounds.width - card.width) / 2, bounds.height - card.height - 16]);
    if (!mobile) {
      const candidates = [
        [x + host.width + 8, y], [x - card.width - 8, y],
        [centerX, y + host.height + 8], [centerX, y - card.height - 8],
      ].map(clamp);
      const dots = others.filter((other) => other.closest('.hotspots-container') === container)
        .map((other) => other.getBoundingClientRect()).filter((dot) => dot.width > 0 && dot.height > 0);
      placement = candidates.find(([left, top]) => !dots.some((dot) =>
        left + bounds.left < dot.right && left + bounds.left + card.width > dot.left &&
        top + bounds.top < dot.bottom && top + bounds.top + card.height > dot.top)) || candidates[1];
    }
    const [left, top] = placement;
    this.style.setProperty('--preview-left', `${left + bounds.left - host.left}px`);
    this.style.setProperty('--preview-top', `${top + bounds.top - host.top}px`);
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  close(restoreFocus = false) {
    this.clearTimers();
    this.pinned = false;
    this.dialog?.close();
    this.trigger?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.trigger.focus({ preventScroll: true });
  }

  clearTimers() {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.close();
  }
}

if (!customElements.get('volt-product-hotspot-preview')) {
  customElements.define('volt-product-hotspot-preview', VoltProductHotspotPreview);
}
